import React, { useState, useEffect } from 'react';
import { collection, query, where, limit, doc, updateDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

interface Notification {
  id: string;
  type: 'nudge_received' | 'practice_reminder' | 'badge_earned';
  fromUserId: string;
  fromUserNickname: string;
  toUserId: string;
  message: string;
  reason?: string;
  timestamp: any;
  read: boolean;
}

interface NotificationSystemProps {
  currentUser: any;
}

const NotificationSystem: React.FC<NotificationSystemProps> = ({ currentUser }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [loading, setLoading] = useState(true);

  // Request notification permission on component mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(permission => {
        console.log('Notification permission:', permission);
      });
    }
  }, []);

  // Load notifications
  useEffect(() => {
    if (!currentUser) return;

    const loadNotifications = () => {
      try {
        const notificationsQuery = query(
          collection(db, 'notifications'),
          where('toUserId', '==', currentUser.uid),
          limit(20)
        );

        // Set up real-time listener for notifications
        const unsubscribe = onSnapshot(notificationsQuery, (snapshot) => {
          const notificationList: Notification[] = [];
          
          snapshot.forEach(doc => {
            const notificationData = {
              id: doc.id,
              ...doc.data()
            } as Notification;
            notificationList.push(notificationData);
          });

          // Sort by timestamp descending (newest first)
          notificationList.sort((a, b) => {
            const aTime = a.timestamp?.toDate?.()?.getTime() || new Date(a.timestamp || 0).getTime();
            const bTime = b.timestamp?.toDate?.()?.getTime() || new Date(b.timestamp || 0).getTime();
            return bTime - aTime;
          });

          setNotifications(notificationList);
          setUnreadCount(notificationList.filter(n => !n.read).length);
          setLoading(false);
        }, (error) => {
          console.error('Error loading notifications:', error);
          setLoading(false);
        });

        // Return unsubscribe function for cleanup
        return unsubscribe;
      } catch (error) {
        console.error('Error setting up notifications listener:', error);
        setLoading(false);
        return () => {}; // Return empty function if error
      }
    };

    const unsubscribe = loadNotifications();
    
    // Cleanup listener on unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [currentUser]);

  // Mark notification as read
  const markAsRead = async (notificationId: string) => {
    try {
      const notificationRef = doc(db, 'notifications', notificationId);
      await updateDoc(notificationRef, { read: true });
      
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      const unreadNotifications = notifications.filter(n => !n.read);
      const promises = unreadNotifications.map(n => 
        updateDoc(doc(db, 'notifications', n.id), { read: true })
      );
      
      await Promise.all(promises);
      
      setNotifications(prev => 
        prev.map(n => ({ ...n, read: true }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  if (loading) return null;

  return (
    <div className="relative">
      {/* Notification Bell Icon with Tooltip */}
      <div className="relative group">
        <button
          onClick={() => setShowNotifications(!showNotifications)}
          className="relative p-2 text-white hover:text-yellow-200 transition-colors bg-white/10 hover:bg-white/20 rounded-lg backdrop-blur-sm"
        >
          {/* Notification Bell */}
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2a10 10 0 100 20 10 10 0 000-20z" />
          </svg>

          {/* Unread Badge */}
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>

        {/* Tooltip */}
        <div className="absolute top-full right-0 mt-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
          Notifications
          <div className="absolute bottom-full right-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-900"></div>
        </div>
      </div>

      {/* Notification Dropdown */}
      {showNotifications && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 overflow-y-auto">
          <div className="p-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
              {notifications.filter(n => !n.read).length > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Mark all read
                </button>
              )}
            </div>
          </div>

          {notifications.filter(n => !n.read).length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              No unread notifications
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {notifications.filter(n => !n.read).map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                    !notification.read ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                  }`}
                  onClick={() => markAsRead(notification.id)}
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      {notification.type === 'nudge_received' && (
                        <span className="text-2xl">🤗</span>
                      )}
                      {notification.type === 'practice_reminder' && (
                        <span className="text-2xl">🎵</span>
                      )}
                      {notification.type === 'badge_earned' && (
                        <span className="text-2xl">🏅</span>
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {notification.fromUserNickname}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">
                        {notification.message}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {notification.timestamp?.toDate ? 
                          notification.timestamp.toDate().toLocaleString() :
                          'Recently'
                        }
                      </p>
                    </div>

                    {!notification.read && (
                      <div className="flex-shrink-0">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationSystem;
